// Placeholder 3D viewport for the Hook Editor. Renders a neutral product
// volume (wireframe unit box on a ground grid) and a gizmo per hook — sphere
// for HookPoint, segment for HookLine, plus a facing arrow (+Z rotated by the
// hook's quaternion). Click a gizmo to select; drag to orbit; wheel to zoom.
// A toolbar switches between the perspective camera and axis-aligned
// orthographic views (Top/Bottom/Left/Right/Front/Back), 3D-editor style.
//
// Deliberately NOT a placement editor: the real 3D placement affordance is the
// planned Unity/WebGL editor (see UnityEditorSeam). This viewport only has to
// visualize positions/orientations well enough that numeric inspector edits
// are verifiable.
//
// Orbit/zoom are hand-rolled (spherical coordinates around a target) instead
// of importing three/examples OrbitControls — the repo doesn't use examples/jsm
// anywhere and we keep it that way to avoid a new import surface. Orbiting in
// an orthographic view keeps the orthographic projection ("user ortho", like
// Blender) and just clears the axis-view highlight.

import { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import type { ProductHookDto } from "../types";
import { UnityEditorSeam } from "../UnityEditorSeam";

export type HookViewName = "persp" | "top" | "bottom" | "left" | "right" | "front" | "back";

const VIEW_BUTTONS: { key: HookViewName; label: string }[] = [
  { key: "persp", label: "Persp" },
  { key: "top", label: "Top" },
  { key: "bottom", label: "Bottom" },
  { key: "left", label: "Left" },
  { key: "right", label: "Right" },
  { key: "front", label: "Front" },
  { key: "back", label: "Back" },
];

export interface HookViewportProps {
  hooks: ProductHookDto[];
  selectedId: string | null;
  onSelect: (hookId: string | null) => void;
}

/** Imperative engine handle stored in a ref — the React layer only pushes
 *  hook data / view changes into it and receives selection clicks back. */
interface ViewportEngine {
  setHooks(hooks: ProductHookDto[], selectedId: string | null): void;
  setView(view: HookViewName): void;
  dispose(): void;
}

const COLOR_HOOK = 0x4da3ff;
const COLOR_HOOK_SELECTED = 0xffb020;
const COLOR_ARROW = 0x9ae66e;
const COLOR_ARROW_SELECTED = 0xffd77a;

// Spherical camera presets. phi is clamped away from the poles so lookAt with a
// constant (0,1,0) up vector never degenerates — Top/Bottom sit an epsilon off
// the axis, which is visually indistinguishable and keeps one orbit model for
// every view.
const PHI_EPS = 0.015;
const VIEW_ANGLES: Record<Exclude<HookViewName, "persp">, { theta: number; phi: number }> = {
  top: { theta: 0, phi: PHI_EPS },
  bottom: { theta: 0, phi: Math.PI - PHI_EPS },
  front: { theta: 0, phi: Math.PI / 2 },
  back: { theta: Math.PI, phi: Math.PI / 2 },
  right: { theta: Math.PI / 2, phi: Math.PI / 2 },
  left: { theta: -Math.PI / 2, phi: Math.PI / 2 },
};

function disposeObject(obj: THREE.Object3D) {
  obj.traverse(child => {
    const mesh = child as THREE.Mesh;
    if (mesh.geometry) mesh.geometry.dispose();
    const mat = mesh.material as THREE.Material | THREE.Material[] | undefined;
    if (Array.isArray(mat)) mat.forEach(m => m.dispose());
    else if (mat) mat.dispose();
  });
}

function createEngine(
  container: HTMLDivElement,
  callbacks: { onPick: (hookId: string | null) => void; onUserOrbit: () => void },
): ViewportEngine {
  const renderer = new THREE.WebGLRenderer({ antialias: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.domElement.className = "hke-viewport__canvas";
  container.appendChild(renderer.domElement);

  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0x22272e);

  scene.add(new THREE.AmbientLight(0xffffff, 1.4));
  const keyLight = new THREE.DirectionalLight(0xffffff, 1.2);
  keyLight.position.set(3, 5, 2);
  scene.add(keyLight);

  // ── Neutral placeholder product volume: 1×1×1 wireframe box sitting on the
  //    grid, root at the bottom-center (hooks are local under the product root).
  const boxGeom = new THREE.BoxGeometry(1, 1, 1);
  const boxEdges = new THREE.EdgesGeometry(boxGeom);
  boxGeom.dispose();
  const box = new THREE.LineSegments(
    boxEdges,
    new THREE.LineBasicMaterial({ color: 0x8899aa, transparent: true, opacity: 0.8 }),
  );
  box.position.set(0, 0.5, 0);
  scene.add(box);

  const grid = new THREE.GridHelper(4, 16, 0x55606c, 0x39424c);
  scene.add(grid);
  const axes = new THREE.AxesHelper(0.75);
  scene.add(axes);

  const gizmoRoot = new THREE.Group();
  scene.add(gizmoRoot);
  let pickables: THREE.Mesh[] = [];

  // ── Cameras. One spherical orbit state drives both; ortho views are just
  //    presets on the same state plus a projection switch.
  const target = new THREE.Vector3(0, 0.5, 0);
  const orbit = { theta: Math.PI / 5, phi: Math.PI / 3, radius: 3.2 };
  let orthoHalfHeight = 1.4; // wheel-zoom adjusts this in ortho mode
  let mode: "persp" | "ortho" = "persp";

  const persp = new THREE.PerspectiveCamera(50, 1, 0.01, 100);
  const ortho = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.01, 100);

  function aspect(): number {
    return container.clientHeight > 0 ? container.clientWidth / Math.max(1, container.clientHeight) : 1;
  }

  function activeCamera(): THREE.Camera {
    return mode === "persp" ? persp : ortho;
  }

  function applyCamera() {
    const sinPhi = Math.sin(orbit.phi);
    const pos = new THREE.Vector3(
      orbit.radius * sinPhi * Math.sin(orbit.theta),
      orbit.radius * Math.cos(orbit.phi),
      orbit.radius * sinPhi * Math.cos(orbit.theta),
    ).add(target);
    const cam = mode === "persp" ? persp : ortho;
    cam.up.set(0, 1, 0);
    cam.position.copy(pos);
    cam.lookAt(target);
    if (mode === "ortho") {
      const halfW = orthoHalfHeight * aspect();
      ortho.left = -halfW;
      ortho.right = halfW;
      ortho.top = orthoHalfHeight;
      ortho.bottom = -orthoHalfHeight;
      ortho.updateProjectionMatrix();
    } else {
      persp.aspect = aspect();
      persp.updateProjectionMatrix();
    }
  }

  function resize() {
    const w = Math.max(1, container.clientWidth);
    const h = Math.max(1, container.clientHeight);
    renderer.setSize(w, h);
    applyCamera();
  }
  resize();

  const observer = new ResizeObserver(() => resize());
  observer.observe(container);

  // ── Render loop ──────────────────────────────────────────────────────
  let raf = 0;
  let disposed = false;
  function loop() {
    if (disposed) return;
    renderer.render(scene, activeCamera());
    raf = requestAnimationFrame(loop);
  }
  raf = requestAnimationFrame(loop);

  // ── Pointer interaction: drag = orbit, click (no drag) = pick ───────
  const raycaster = new THREE.Raycaster();
  let dragging = false;
  let dragMoved = false;
  let lastX = 0;
  let lastY = 0;

  function onPointerDown(e: PointerEvent) {
    if (e.button !== 0) return;
    dragging = true;
    dragMoved = false;
    lastX = e.clientX;
    lastY = e.clientY;
    renderer.domElement.setPointerCapture(e.pointerId);
  }

  function onPointerMove(e: PointerEvent) {
    if (!dragging) return;
    const dx = e.clientX - lastX;
    const dy = e.clientY - lastY;
    if (!dragMoved && Math.abs(e.clientX - lastX) + Math.abs(e.clientY - lastY) < 3) return;
    dragMoved = true;
    lastX = e.clientX;
    lastY = e.clientY;
    orbit.theta -= dx * 0.008;
    orbit.phi = Math.min(Math.PI - PHI_EPS, Math.max(PHI_EPS, orbit.phi - dy * 0.008));
    applyCamera();
    callbacks.onUserOrbit();
  }

  function onPointerUp(e: PointerEvent) {
    if (!dragging) return;
    dragging = false;
    renderer.domElement.releasePointerCapture(e.pointerId);
    if (dragMoved) return; // it was an orbit, not a click
    // Raycast pick against the hook gizmo hit-meshes.
    const rect = renderer.domElement.getBoundingClientRect();
    const ndc = new THREE.Vector2(
      ((e.clientX - rect.left) / rect.width) * 2 - 1,
      -((e.clientY - rect.top) / rect.height) * 2 + 1,
    );
    raycaster.setFromCamera(ndc, activeCamera());
    const hits = raycaster.intersectObjects(pickables, false);
    callbacks.onPick(hits.length > 0 ? (hits[0].object.userData.hookId as string) : null);
  }

  function onWheel(e: WheelEvent) {
    e.preventDefault();
    const factor = Math.exp(e.deltaY * 0.001);
    if (mode === "persp") {
      orbit.radius = Math.min(25, Math.max(0.4, orbit.radius * factor));
    } else {
      orthoHalfHeight = Math.min(20, Math.max(0.2, orthoHalfHeight * factor));
    }
    applyCamera();
  }

  renderer.domElement.addEventListener("pointerdown", onPointerDown);
  renderer.domElement.addEventListener("pointermove", onPointerMove);
  renderer.domElement.addEventListener("pointerup", onPointerUp);
  renderer.domElement.addEventListener("wheel", onWheel, { passive: false });

  // ── Gizmo construction ───────────────────────────────────────────────
  const sphereGeom = new THREE.SphereGeometry(0.035, 16, 12);
  const hitSphereGeom = new THREE.SphereGeometry(0.09, 8, 6);

  function buildGizmos(hooks: ProductHookDto[], selectedId: string | null) {
    for (const child of [...gizmoRoot.children]) {
      gizmoRoot.remove(child);
      disposeObject(child);
    }
    pickables = [];

    for (const hook of hooks) {
      const selected = hook.id === selectedId;
      const color = selected ? COLOR_HOOK_SELECTED : COLOR_HOOK;
      const group = new THREE.Group();
      group.position.set(hook.posX, hook.posY, hook.posZ);
      group.quaternion.set(hook.rotX, hook.rotY, hook.rotZ, hook.rotW).normalize();

      if (hook.kind === "HookPoint") {
        const sphere = new THREE.Mesh(sphereGeom.clone(), new THREE.MeshStandardMaterial({ color }));
        if (selected) sphere.scale.setScalar(1.35);
        group.add(sphere);
        // Larger invisible hit target so small spheres are clickable.
        const hit = new THREE.Mesh(hitSphereGeom.clone(), new THREE.MeshBasicMaterial({ visible: false }));
        hit.userData.hookId = hook.id;
        group.add(hit);
        pickables.push(hit);
      } else {
        // HookLine: a segment of `width` along the hook's local X axis,
        // centered on pos (facing stays +Z, perpendicular to the strip).
        const width = hook.width ?? 1.0;
        const bar = new THREE.Mesh(
          new THREE.BoxGeometry(Math.max(0.02, width), 0.022, 0.022),
          new THREE.MeshStandardMaterial({ color }),
        );
        if (selected) bar.scale.set(1, 1.6, 1.6);
        group.add(bar);
        // End caps make the segment read as a strip, not a stray wire.
        for (const sign of [-1, 1]) {
          const cap = new THREE.Mesh(
            new THREE.SphereGeometry(0.025, 10, 8),
            new THREE.MeshStandardMaterial({ color }),
          );
          cap.position.set((sign * width) / 2, 0, 0);
          group.add(cap);
        }
        const hit = new THREE.Mesh(
          new THREE.BoxGeometry(Math.max(0.1, width), 0.12, 0.12),
          new THREE.MeshBasicMaterial({ visible: false }),
        );
        hit.userData.hookId = hook.id;
        group.add(hit);
        pickables.push(hit);
      }

      // Facing arrow: local +Z, i.e. the mate direction after the quaternion.
      const arrow = new THREE.ArrowHelper(
        new THREE.Vector3(0, 0, 1),
        new THREE.Vector3(0, 0, 0),
        0.22,
        selected ? COLOR_ARROW_SELECTED : COLOR_ARROW,
        0.07,
        0.035,
      );
      group.add(arrow);

      gizmoRoot.add(group);
    }
  }

  return {
    setHooks: buildGizmos,
    setView(view: HookViewName) {
      if (view === "persp") {
        mode = "persp";
        // Keep the current orbit angles — switching projection shouldn't jump
        // the camera; nudge phi off any pole preset so perspective feels 3D.
        if (orbit.phi < 0.1) orbit.phi = Math.PI / 3;
        if (orbit.phi > Math.PI - 0.1) orbit.phi = Math.PI - Math.PI / 3;
      } else {
        mode = "ortho";
        const angles = VIEW_ANGLES[view];
        orbit.theta = angles.theta;
        orbit.phi = angles.phi;
      }
      applyCamera();
    },
    dispose() {
      disposed = true;
      cancelAnimationFrame(raf);
      observer.disconnect();
      renderer.domElement.removeEventListener("pointerdown", onPointerDown);
      renderer.domElement.removeEventListener("pointermove", onPointerMove);
      renderer.domElement.removeEventListener("pointerup", onPointerUp);
      renderer.domElement.removeEventListener("wheel", onWheel);
      disposeObject(scene);
      sphereGeom.dispose();
      hitSphereGeom.dispose();
      renderer.dispose();
      renderer.domElement.remove();
    },
  };
}

export function HookViewport(props: HookViewportProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const engineRef = useRef<ViewportEngine | null>(null);
  // Latest onSelect without re-creating the engine when the callback identity changes.
  const onSelectRef = useRef(props.onSelect);
  onSelectRef.current = props.onSelect;

  const [activeView, setActiveView] = useState<HookViewName | null>("persp");

  // Engine lifecycle — created once, disposed on unmount (StrictMode-safe:
  // the second mount just creates a fresh engine after the first disposed).
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const engine = createEngine(container, {
      onPick: id => onSelectRef.current(id),
      // A manual orbit leaves any axis-aligned view ("user ortho") — drop the
      // axis-button highlight but keep the projection mode.
      onUserOrbit: () => setActiveView(prev => (prev === "persp" ? prev : null)),
    });
    engineRef.current = engine;
    return () => {
      engineRef.current = null;
      engine.dispose();
    };
  }, []);

  // Push hook data + selection into the scene whenever they change.
  useEffect(() => {
    engineRef.current?.setHooks(props.hooks, props.selectedId);
  }, [props.hooks, props.selectedId]);

  function switchView(view: HookViewName) {
    engineRef.current?.setView(view);
    setActiveView(view);
  }

  return (
    <div className="hke-viewport">
      <div ref={containerRef} className="hke-viewport__canvas-host" />
      <div className="hke-viewport__toolbar" role="toolbar" aria-label="Viewport camera">
        {VIEW_BUTTONS.map(btn => (
          <button
            key={btn.key}
            type="button"
            className={
              "hke-viewport__view-btn" + (activeView === btn.key ? " hke-viewport__view-btn--active" : "")
            }
            onClick={() => switchView(btn.key)}
          >
            {btn.label}
          </button>
        ))}
      </div>
      <div className="hke-viewport__banner">Product model preview — Unity 3D editor mounts here</div>
      <div className="hke-viewport__seam-slot">
        <UnityEditorSeam />
      </div>
      <div className="hke-viewport__hint">Drag to orbit · wheel to zoom · click a hook to select</div>
    </div>
  );
}

export default HookViewport;
